import {
  ArrowUp,
  Box,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Link2,
  LoaderCircle,
  Mic,
  MoreHorizontal,
  Paperclip,
  Plus,
  Settings2,
  Sparkles,
  Zap,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
} from "react";

import type { DashboardSection } from "./DashboardSidebar";

type AgentAction = {
  id: string;
  title: string;
  description: string;
  meta: string;
  status: "complete" | "running" | "pending";
  section: DashboardSection;
  detail: {
    label: string;
    lines: string[];
  };
};

const agentActions: ReadonlyArray<AgentAction> = [
  {
    id: "competitor-scan",
    title: "Researching 18 competitor reels",
    description: "Scanned creators in your niche and adjacent spaces",
    meta: "Done · 10:43 AM",
    status: "complete",
    section: "research",
    detail: {
      label: "research output",
      lines: [
        "6 repeatable hooks saved",
        "3 product-led formats matched to your brand",
        "4 off-brand trends filtered out",
      ],
    },
  },
  {
    id: "format-analysis",
    title: "Found 6 viral formats",
    description: "Identified recurring hooks, structures, and CTAs",
    meta: "Done · 10:44 AM",
    status: "complete",
    section: "research",
    detail: {
      label: "format analysis",
      lines: [
        "ranked by repeatability and audience fit",
        "top signal: useful list + product in action",
      ],
    },
  },
  {
    id: "content-generation",
    title: "Drafting 4 reel concepts",
    description: "Writing hooks, captions, and on-screen text",
    meta: "In progress",
    status: "running",
    section: "pipeline",
    detail: {
      label: "content output",
      lines: [
        "2 product education concepts",
        "1 founder point of view",
        "1 trend adaptation",
      ],
    },
  },
  {
    id: "schedule-check",
    title: "Scheduling 7 posts",
    description: "Optimizing times and adding approved work to the calendar",
    meta: "Pending",
    status: "pending",
    section: "calendar",
    detail: {
      label: "scheduler",
      lines: [
        "Tuesday, Thursday, and Saturday prioritized",
        "publishing waits for final approval",
      ],
    },
  },
] as const;

const useAutoResizeTextarea = ({
  minHeight,
  maxHeight,
}: {
  minHeight: number;
  maxHeight: number;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(
    (reset = false) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.style.height = `${minHeight}px`;
      if (reset) return;

      textarea.style.height = `${Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight),
      )}px`;
    },
    [maxHeight, minHeight],
  );

  useEffect(() => {
    adjustHeight(true);
  }, [adjustHeight]);

  return { textareaRef, adjustHeight };
};

const AgentToolCalls = ({
  onSelect,
}: {
  onSelect: (section: DashboardSection) => void;
}) => {
  const [expandedAction, setExpandedAction] = useState<string | null>(null);

  return (
    <div className="agent-tool-calls" aria-label="Agent actions">
      {agentActions.map((action) => {
        const expanded = expandedAction === action.id;
        const detailId = `${action.id}-detail`;

        return (
          <div
            key={action.id}
            className={expanded ? "agent-tool-call is-expanded" : "agent-tool-call"}
          >
            <button
              type="button"
              className="agent-tool-call__row"
              aria-expanded={expanded}
              aria-controls={detailId}
              onClick={() =>
                setExpandedAction((current) =>
                  current === action.id ? null : action.id,
                )
              }
            >
              <span
                className={`agent-tool-call__status is-${action.status}`}
                aria-label={
                  action.status === "complete"
                    ? "Complete"
                    : action.status === "running"
                      ? "Running"
                      : "Pending"
                }
                role="status"
              >
                {action.status === "complete" ? (
                  <Check size={12} aria-hidden="true" />
                ) : action.status === "running" ? (
                  <LoaderCircle size={13} aria-hidden="true" />
                ) : (
                  <Circle size={10} aria-hidden="true" />
                )}
              </span>
              <span className="agent-tool-call__copy">
                <strong>{action.title}</strong>
                <small>{action.description}</small>
              </span>
              <span
                className={`agent-tool-call__meta is-${action.status}`}
              >
                {action.meta}
              </span>
              <ChevronDown
                className={expanded ? "is-open" : ""}
                size={13}
                aria-hidden="true"
              />
            </button>

            {expanded ? (
              <div id={detailId} className="agent-tool-call__detail">
                <span>{action.detail.label}</span>
                <pre>{action.detail.lines.join("\n")}</pre>
                <button
                  type="button"
                  onClick={() => onSelect(action.section)}
                >
                  Open in Reelio
                  <ChevronRight size={12} aria-hidden="true" />
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

type AgentHomeProps = {
  workspaceName: string;
  instagramHandle: string;
  focusRequest: number;
  onSelect: (section: DashboardSection) => void;
};

export const AgentHome = ({
  workspaceName,
  instagramHandle,
  focusRequest,
  onSelect,
}: AgentHomeProps) => {
  const [message, setMessage] = useState("");
  const [latestBrief, setLatestBrief] = useState(
    `Launch a 7-day Instagram growth sprint for ${workspaceName}. Research competitors, find viral formats, create 4 reel concepts, write captions, and schedule posts.`,
  );
  const [briefTime, setBriefTime] = useState("10:42 AM");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [connectorOpen, setConnectorOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { textareaRef, adjustHeight } = useAutoResizeTextarea({
    minHeight: 44,
    maxHeight: 132,
  });

  useEffect(() => {
    if (focusRequest === 0) return;
    textareaRef.current?.focus();
  }, [focusRequest, textareaRef]);

  const submitBrief = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed) return;

    setLatestBrief(trimmed);
    setBriefTime("Just now");
    setFeedback(
      `Brief received. Reelio is applying it to @${instagramHandle}.`,
    );
    setMessage("");
    setAttachments([]);
    adjustHeight(true);
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  const onFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const names = Array.from(event.target.files ?? []).map((file) => file.name);
    setAttachments(names);
    setFeedback(
      names.length
        ? `${names.length} brand ${names.length === 1 ? "asset" : "assets"} attached.`
        : "",
    );
  };

  return (
    <section className="agent-home" aria-label="Reelio agent home">
      <div className="agent-home__thread">
        <header className="agent-home__thread-header">
          <div>
            <button type="button" aria-label="Choose agent run">
              Instagram growth sprint
              <ChevronDown size={14} aria-hidden="true" />
            </button>
            <span>
              <i />
              Running
            </span>
          </div>
          <div>
            <button type="button" aria-label="Thread settings">
              <Settings2 size={16} aria-hidden="true" />
            </button>
            <button type="button" aria-label="More thread actions">
              <MoreHorizontal size={17} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="agent-home__transcript">
          <div className="agent-home__conversation">
            <article className="agent-home__user-message">
              <header>
                <span>You</span>
                <time>{briefTime}</time>
              </header>
              <p>{latestBrief}</p>
            </article>

            <article className="agent-home__agent-message">
              <header>
                <span className="agent-home__agent-avatar">
                  <Sparkles size={14} aria-hidden="true" />
                </span>
                <span>
                  <strong>Reelio</strong>
                  <time>10:42 AM</time>
                </span>
              </header>

              <div className="agent-home__agent-response">
                <p>
                  On it. I’ll run research, surface winning formats, draft
                  concepts with captions, and schedule your 7-day plan.
                </p>

                <AgentToolCalls onSelect={onSelect} />

                <p>Here’s a draft concept for your review.</p>

                <button
                  type="button"
                  className="agent-home__concept"
                  onClick={() => onSelect("pipeline")}
                  aria-label="Open reel concept"
                >
                  <img
                    src="/assets/reelio-saved-ideas-reel.png"
                    alt="Formasty Reel concept: 3 content ideas that actually get saved"
                  />
                  <div className="agent-home__concept-copy">
                    <small>Concept 1 of 4</small>
                    <div>
                      <small>Hook</small>
                      <strong>Stop posting for likes. Start posting for saves.</strong>
                    </div>
                    <div>
                      <small>Caption</small>
                      <p>
                        Here are 3 content ideas your audience will actually
                        save and come back to.
                        <br />
                        <br />
                        Which one will you try first?
                      </p>
                    </div>
                    <em>
                      #contentstrategy #instagramgrowth #contentideas
                    </em>
                    <div className="agent-home__concept-stats">
                      <span>
                        <small>Format</small>
                        <strong>Listicle</strong>
                      </span>
                      <span>
                        <small>Length</small>
                        <strong>15s</strong>
                      </span>
                      <span>
                        <small>Goal</small>
                        <strong>Saves</strong>
                      </span>
                    </div>
                  </div>
                </button>

                <p className="agent-home__continuation">
                  More concepts coming up next.
                </p>
              </div>
            </article>
          </div>
        </div>

        <div className="agent-home__composer-dock">
          <form className="agent-home__composer" onSubmit={submitBrief}>
            <textarea
              id="agent-brief"
              ref={textareaRef}
              value={message}
              rows={1}
              aria-label="Brief your content team"
              placeholder="Ask Reelio anything…"
              onChange={(event) => {
                setMessage(event.target.value);
                setFeedback("");
                adjustHeight();
              }}
              onKeyDown={onComposerKeyDown}
            />

            {attachments.length ? (
              <div
                className="agent-home__attachments"
                aria-label="Attached files"
              >
                {attachments.map((file) => (
                  <span key={file}>{file}</span>
                ))}
              </div>
            ) : null}

            <div className="agent-home__composer-footer">
              <div>
                <button
                  type="button"
                  aria-label="Add brand assets"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Plus size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Attach files"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip size={17} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="agent-home__composer-label"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Box size={15} aria-hidden="true" />
                  Brand assets
                </button>
                <div className="agent-home__connector-wrap">
                  <button
                    type="button"
                    className="agent-home__composer-label"
                    aria-expanded={connectorOpen}
                    onClick={() => setConnectorOpen((open) => !open)}
                  >
                    <Link2 size={15} aria-hidden="true" />
                    Connectors
                    <ChevronDown size={12} aria-hidden="true" />
                  </button>
                  {connectorOpen ? (
                    <div
                      className="agent-home__connector-popover"
                      role="dialog"
                      aria-label="Connectors"
                    >
                      <strong>Connectors</strong>
                      <button
                        type="button"
                        onClick={() => {
                          setConnectorOpen(false);
                          onSelect("brand");
                        }}
                      >
                        Instagram
                        <span>Connect</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setConnectorOpen(false);
                          onSelect("brand");
                        }}
                      >
                        Shopify
                        <span>Add</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div>
                <button
                  type="button"
                  className="agent-home__mode"
                  onClick={() => setFeedback("Autopilot mode is selected.")}
                >
                  <Zap size={14} aria-hidden="true" />
                  Auto
                  <ChevronDown size={12} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Voice to text"
                  onClick={() => setFeedback("Voice input is coming soon.")}
                >
                  <Mic size={16} aria-hidden="true" />
                </button>
                <button
                  type="submit"
                  className="agent-home__send"
                  aria-label="Send brief"
                  disabled={!message.trim()}
                >
                  <ArrowUp size={17} aria-hidden="true" />
                </button>
              </div>
            </div>

            <input
              ref={fileInputRef}
              className="agent-home__file-input"
              type="file"
              multiple
              accept="image/*,video/*,.pdf,.doc,.docx"
              onChange={onFilesSelected}
            />
          </form>
          <p className="agent-home__feedback" aria-live="polite">
            {feedback}
          </p>
        </div>
      </div>

      <aside className="agent-home__context" aria-label="Thread context">
        <section>
          <Sparkles size={18} aria-hidden="true" />
          <h2>Thread context</h2>
          <p>
            Key details, assets, and decisions from this conversation will
            appear here for easy reference.
          </p>
        </section>
        <section>
          <Box size={18} aria-hidden="true" />
          <h2>Next steps</h2>
          <p>Upcoming actions and suggestions will show here as we go.</p>
        </section>
      </aside>
    </section>
  );
};
